import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PageMeta, JsonLd } from '@/components/feature/Head';
import { categories, getArticlesByCategory, getCategoryBySlug } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'film-tv');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const category = getCategoryBySlug(activeSlug);
  const categoryArticles = getArticlesByCategory(activeSlug);

  const pageTitle = category
    ? `${category.name} - 国产区精选内容聚合平台`
    : '分类浏览 - 国产区精选内容聚合平台';

  const pageDescription = category
    ? `${category.description}。国产区${category.name}分类汇聚优质国产原创内容，为您提供最全面的国产区资源推荐。`
    : '国产区分类浏览页面，提供影视精选、文学创作、音乐之声、视觉艺术、科技前沿、生活方式、历史文化、游戏动漫等八大内容分类。';

  return (
    <>
      <PageMeta
        title={pageTitle}
        description={pageDescription}
        keywords={`国产区,国产区${category?.name || '分类'},国产区资源,国产区内容推荐`}
        canonical={`https://guochanqu.com/category/${activeSlug}`}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: pageTitle,
          description: category?.description || '',
          url: `https://guochanqu.com/category/${activeSlug}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '国产区精选内容聚合平台',
            url: 'https://guochanqu.com',
          },
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://guochanqu.com' },
            { '@type': 'ListItem', position: 2, name: category?.name || '分类浏览', item: `https://guochanqu.com/category/${activeSlug}` },
          ],
        }}
      />

      <main className="pt-20 min-h-screen bg-background-50">
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">
                <i className="ri-home-line mr-1"></i>首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800">{category?.name || '分类浏览'}</span>
            </nav>

            <div className="flex flex-wrap items-center gap-2 mb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          {category && (
            <div className="mb-10">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-2">
                {category.name}
              </h1>
              <p className="text-sm text-foreground-600">{category.description}</p>
            </div>
          )}

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group flex flex-col bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-background-300/60 hover:scale-[1.02] transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-background-50/80 backdrop-blur-sm text-xs text-foreground-700">
                      <i className="ri-eye-line text-xs"></i>
                      {article.viewCount > 10000 ? `${(article.viewCount / 10000).toFixed(1)}万` : article.viewCount}
                    </div>
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="text-base font-medium text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 mb-3">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed mb-4 flex-1">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-400 pt-3 border-t border-background-200/70">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line"></i> {article.author}
                      </span>
                      <span>{article.publishDate}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}